/**
 * Range Filter Service
 *
 * Shared match rule for every Filters modal field shaped `{ minimum, maximum }` (Price, Bedrooms,
 * Bathrooms, Square Feet — see redux/STORE_DESIGN.md#range-filter-semantics-price-bedroom-bathroom-size).
 * `minimum`/`maximum` are independently nullable: both null means no filter: always matches,
 * regardless of whether the entity itself has a value for that field. Once either bound is set,
 * an entity with no value for that field (null/undefined) can't satisfy it and never matches.
 *
 * @module nativeInteractiveSiteMap/services/rangeFilterService
 */

/**
 * @param {number|null|undefined} value - The entity's own value for this field (e.g. a floor
 *   plan's `price`), or null/undefined when the entity has none.
 * @param {{ minimum: number|null, maximum: number|null }} range
 * @returns {boolean}
 */
export default function matchesRange(value, { minimum, maximum }) {
  if (minimum == null && maximum == null) {
    return true;
  }

  if (value == null) {
    return false;
  }

  if (minimum != null && value < minimum) {
    return false;
  }

  if (maximum != null && value > maximum) {
    return false;
  }

  return true;
}

/**
 * matchesRange for count fields picked from a Smart Range Selection (Bedrooms, Bathrooms): the
 * options are whole numbers only and half values (.5) are hidden from the user, so a half value
 * counts as its whole number — "exactly 3" bathrooms matches 3 and 3.5, "3–4" matches 3 through
 * 4.5, and "5+" matches 5.5.
 *
 * @param {number|null|undefined} value
 * @param {{ minimum: number|null, maximum: number|null }} range
 * @returns {boolean}
 */
export function matchesCountRange(value, range) {
  return matchesRange(value == null ? value : Math.floor(value), range);
}
